import { useMutation } from '@apollo/client';
import { useRouter, useShowToast } from '@application/utils';
import { graphql } from '@gql/gql';
import type {
  MtSocialProvidersLinkAccountMutation,
  MtSocialProvidersLinkAccountMutationVariables,
} from '@gql/graphql';
import type { MtSocialProvidersLinkEmailFormValues } from '@presentation/components';
import { useCallback } from 'react';
import { MT_SOCIAL_PROVIDERS_ACCOUNT_PROVIDERS } from './useAccountProviders';

export const MT_SOCIAL_PROVIDERS_LINK_ACCOUNT = graphql(`
  mutation MtSocialProvidersLinkAccount(
    $email: String
    $password: String
    $googleToken: String
    $facebookToken: String
  ) {
    linkAccount(
      email: $email
      password: $password
      googleToken: $googleToken
      facebookToken: $facebookToken
    ) {
      id
    }
  }
`);

export const useLinkAccount = () => {
  const { showToast } = useShowToast();
  const { translate } = useRouter();

  const [linkMutation, { error, loading }] = useMutation<
    MtSocialProvidersLinkAccountMutation,
    MtSocialProvidersLinkAccountMutationVariables
  >(MT_SOCIAL_PROVIDERS_LINK_ACCOUNT, {
    awaitRefetchQueries: true,
    refetchQueries: [MT_SOCIAL_PROVIDERS_ACCOUNT_PROVIDERS],
  });

  const link = useCallback(
    async (variables: MtSocialProvidersLinkAccountMutationVariables) => {
      await linkMutation({ variables });
      showToast(translate('common.toasts.linkAccount'), {
        type: 'success',
      });
    },
    [linkMutation, showToast, translate],
  );

  const linkWithGoogleToken = useCallback(
    (googleToken?: string) => {
      if (!googleToken) {
        return showToast(translate('common.toasts.apolloError.auth/google-token-invalid'), {
          type: 'error',
        });
      }
      return link({ googleToken });
    },
    [link, showToast, translate],
  );

  const linkWithFacebookToken = useCallback(
    (facebookToken?: string) => {
      if (!facebookToken) {
        return showToast(translate('common.toasts.apolloError.auth/facebook-token-invalid'), {
          type: 'error',
        });
      }
      return link({ facebookToken });
    },
    [link, showToast, translate],
  );

  const linkWithEmail = useCallback(
    (values: MtSocialProvidersLinkEmailFormValues) => {
      return link({ email: values.email, password: values.password });
    },
    [link],
  );

  return {
    linkWithGoogleToken,
    linkWithFacebookToken,
    linkWithEmail,
    linkAccountLoading: loading,
    linkAccountError: error,
  };
};
